import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Download, Film, History, Image, Loader2, Pause, Play, X, ZoomIn, ZoomOut } from "lucide-react";

import { api, DEFAULT_REPLAY_RATE, type SavedClip } from "../api";
import {
  useRecordingDownloads,
  type RecordingDownloadJob,
} from "../components/RecordingDownloadsContext";
import { ClipScrubber } from "../components/ClipScrubber";
import { Modal } from "../components/Modal";
import { PageHeader } from "../components/PageHeader";
import { PlayerFullscreenButton } from "../components/PlayerFullscreenButton";
import { useScope } from "../components/ScopeContext";
import { ClipPlayerCanvas, useDirectReplay } from "../useDirectReplay";

function chipClass(active: boolean, disabled = false) {
  return (
    "inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium transition " +
    (disabled
      ? "cursor-not-allowed border-white/10 bg-bf-surface/60 text-bf-text opacity-50"
      : active
        ? "border-bf-magenta/40 bg-bf-magenta/15 text-bf-magenta"
        : "border-white/10 bg-bf-surface/60 text-bf-text hover:border-white/20")
  );
}

function clockLabel(ms: number): string {
  return new Date(ms).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function dayLabel(ms: number): string {
  return new Date(ms).toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
}

function rangeLengthLabel(seconds: number): string {
  const s = Math.max(0, seconds);
  if (s < 60) return `${Math.round(s)} sec`;
  const min = Math.round((s / 60) * 10) / 10;
  return Number.isInteger(min) ? `${min} min` : `${min.toFixed(1)} min`;
}

function windowLabel(startMs: number, endMs: number, durationS: number, rate: number): string {
  const sameDay =
    new Date(startMs).toDateString() === new Date(endMs).toDateString();
  const rateBit = rate !== 1 ? ` @ ${rate}x` : "";
  return (
    `${dayLabel(startMs)}` +
    (sameDay ? "" : ` – ${dayLabel(endMs)}`) +
    ` · ${clockLabel(startMs)} – ${clockLabel(endMs)}` +
    ` · ${rangeLengthLabel(durationS)}${rateBit}`
  );
}

function savedAtLabel(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const THUMB_BOX =
  "h-16 w-[7.25rem] shrink-0 overflow-hidden rounded-lg bg-white/[0.04]";

function StillViewer({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(
    null,
  );
  const stageRef = useRef<HTMLDivElement>(null);

  const clampScale = (v: number) => Math.min(5, Math.max(1, v));

  function zoomBy(delta: number) {
    setScale((s) => {
      const next = clampScale(s + delta);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }

  function close() {
    if (document.fullscreenElement) void document.exitFullscreen();
    onClose();
  }

  return (
    <Modal
      onClose={close}
      labelledBy="saved-still-title"
      panelClassName="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden p-3"
    >
      <h2 id="saved-still-title" className="sr-only">
        {alt}
      </h2>
      <div
        ref={stageRef}
        className="relative min-h-0 flex-1 overflow-hidden rounded-md bg-black"
        onWheel={(e) => {
          e.preventDefault();
          zoomBy(e.deltaY < 0 ? 0.2 : -0.2);
        }}
        onPointerDown={(e) => {
          if (scale <= 1) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          dragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
        }}
        onPointerMove={(e) => {
          if (!dragRef.current) return;
          setPan({
            x: dragRef.current.panX + (e.clientX - dragRef.current.x),
            y: dragRef.current.panY + (e.clientY - dragRef.current.y),
          });
        }}
        onPointerUp={() => {
          dragRef.current = null;
        }}
        onPointerCancel={() => {
          dragRef.current = null;
        }}
        onDoubleClick={() => zoomBy(scale < 2 ? 1 : -1)}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="h-full w-full select-none object-contain object-center transition-transform duration-100"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            cursor: scale > 1 ? "grab" : "zoom-in",
          }}
        />
        <div className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded-md bg-black/35 p-1 text-white/80 backdrop-blur">
          <button
            type="button"
            onClick={() => zoomBy(-0.25)}
            className="rounded p-1.5 hover:bg-white/10 hover:text-white"
            aria-label="Zoom out"
          >
            <ZoomOut size={18} />
          </button>
          <span className="min-w-[3rem] text-center text-xs tabular-nums">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            onClick={() => zoomBy(0.25)}
            className="rounded p-1.5 hover:bg-white/10 hover:text-white"
            aria-label="Zoom in"
          >
            <ZoomIn size={18} />
          </button>
          <button
            type="button"
            onClick={close}
            className="rounded p-1.5 hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <PlayerFullscreenButton targetRef={stageRef} />
      </div>
    </Modal>
  );
}

function StillThumb({ src, alt }: { src?: string; alt: string }) {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  if (!src || failed) {
    return <div className={THUMB_BOX} aria-hidden />;
  }
  return (
    <>
      <button
        type="button"
        className={`${THUMB_BOX} block`}
        onClick={() => setOpen(true)}
      >
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-contain"
          onError={() => setFailed(true)}
        />
      </button>
      {open && <StillViewer src={src} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
}

function statusLabel(job: RecordingDownloadJob, elapsedS: number): string {
  if (job.status === "preparing") return `Preparing (${elapsedS}s)`;
  if (job.status === "paused") return "Paused";
  if (job.status === "ready") return "Ready";
  if (job.status === "failed") return "Failed";
  return "Cancelled";
}

function jobWindowMs(job: RecordingDownloadJob): { startMs: number; endMs: number } {
  return {
    startMs: job.startMs - job.preS * 1000,
    endMs: job.endMs + job.postS * 1000,
  };
}

function DownloadRow({ job }: { job: RecordingDownloadJob }) {
  const { cancelDownload, pauseDownload, resumeDownload, dismissDownload } =
    useRecordingDownloads();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (job.status !== "preparing") return;
    const iv = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(iv);
  }, [job.status, job.id]);

  const { startMs, endMs } = jobWindowMs(job);
  const durationS = Math.max(0, Math.round((endMs - startMs) / 1000));
  const elapsedS = Math.max(0, Math.round((now - job.startedAt) / 1000));

  return (
    <div className="bf-card flex flex-wrap items-center gap-x-4 gap-y-3 p-4">
      <StillThumb src={job.thumbUrl} alt={job.cameraName} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-bf-text">
            <Film size={14} />
            Clip
          </span>
          <span className="truncate text-sm text-bf-muted">{job.cameraName}</span>
        </div>
        <div className="mt-1 text-sm text-bf-muted">
          {windowLabel(startMs, endMs, durationS, job.rate)}
        </div>
        <div className="mt-1 text-sm text-bf-muted">
          {statusLabel(job, elapsedS)}
          {job.error ? ` · ${job.error}` : ""}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/recordings"
          search={{ camera: job.cameraId }}
          className={chipClass(false)}
        >
          <span className="inline-flex items-center gap-1.5">
            <History size={16} /> Playback
          </span>
        </Link>
        {job.status === "preparing" && (
          <>
            <button
              type="button"
              className={chipClass(false)}
              onClick={() => pauseDownload(job.id)}
            >
              <span className="inline-flex items-center gap-1.5">
                <Pause size={16} />
                Pause
              </span>
            </button>
            <button
              type="button"
              className={chipClass(false)}
              onClick={() => cancelDownload(job.id)}
            >
              <span className="inline-flex items-center gap-1.5">
                <Loader2 size={16} className="animate-spin" />
                Cancel
              </span>
            </button>
          </>
        )}
        {job.status === "paused" && (
          <>
            <button
              type="button"
              className={chipClass(false)}
              onClick={() => resumeDownload(job.id)}
            >
              <span className="inline-flex items-center gap-1.5">
                <Play size={16} />
                Resume
              </span>
            </button>
            <button
              type="button"
              className={chipClass(false)}
              onClick={() => cancelDownload(job.id)}
            >
              Cancel
            </button>
          </>
        )}
        {job.status === "ready" && job.clip && (
          <a
            href={job.clip.url}
            download={job.clip.filename}
            className={chipClass(true)}
          >
            <span className="inline-flex items-center gap-1.5">
              <Download size={16} /> Save clip
            </span>
          </a>
        )}
        {job.status !== "preparing" && job.status !== "paused" && (
          <button
            type="button"
            className={chipClass(false)}
            aria-label="Remove from list"
            onClick={() => dismissDownload(job.id)}
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

function isImage(clip: SavedClip): boolean {
  if (clip.kind === "image") return true;
  const name = (clip.filename || "").toLowerCase();
  return name.endsWith(".jpg") || name.endsWith(".jpeg");
}

function downloadSavedImage(clip: SavedClip) {
  const link = document.createElement("a");
  link.href = api.downloads.thumbUrl(clip.id, true);
  link.download = clip.filename || `${clip.camera_id}_${clip.start_ms}.jpg`;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function SavedClipPlayer({
  clip,
  onClose,
}: {
  clip: SavedClip;
  onClose: () => void;
}) {
  const replay = useDirectReplay(clip.camera_id);
  const startMs = clip.start_ms || clip.begin_unix * 1000;

  useEffect(() => {
    void replay.seek(startMs);
    // Warm + seek once the modal is up; the hook also opens the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip.id, startMs]);

  return (
    <Modal
      onClose={onClose}
      labelledBy="saved-clip-play-title"
      panelClassName="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden p-3"
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 id="saved-clip-play-title" className="text-sm font-medium text-bf-text">
          {clip.camera_name}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1.5 text-bf-muted hover:bg-white/10 hover:text-bf-text"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-md bg-black">
        <ClipPlayerCanvas
          canvasRef={replay.canvasRef}
          visible={replay.playing && !replay.pending}
          cameraId={clip.camera_id}
          view={clip.view}
        />
        {replay.pending && (
          <div className="absolute inset-0 grid place-items-center">
            <Loader2 size={28} className="animate-spin text-white/80" />
          </div>
        )}
      </div>
      <div className="mt-2">
        <ClipScrubber
          cameraId={clip.camera_id}
          cameraName={clip.camera_name}
          startMs={startMs}
          endMs={clip.end_ms || clip.end_unix * 1000 || startMs}
          replay={replay}
        />
      </div>
    </Modal>
  );
}

function SavedClipRow({
  clip,
  busy,
  onRedownload,
  onRemove,
  onPlay,
}: {
  clip: SavedClip;
  busy: boolean;
  onRedownload: (clip: SavedClip) => void;
  onRemove: (clip: SavedClip) => void;
  onPlay: (clip: SavedClip) => void;
}) {
  const image = isImage(clip);
  const startMs = image ? clip.start_ms : clip.begin_unix * 1000;
  const endMs = clip.end_unix * 1000;
  return (
    <div className="bf-card flex flex-wrap items-center gap-x-4 gap-y-3 p-4">
      <StillThumb src={api.downloads.thumbUrl(clip.id)} alt={clip.camera_name} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-bf-text">
            {image ? <Image size={14} /> : <Film size={14} />}
            {image ? "Image" : "Clip"}
          </span>
          <span className="truncate text-sm text-bf-muted">{clip.camera_name}</span>
        </div>
        <div className="mt-1 text-sm text-bf-muted">
          {image
            ? `${dayLabel(startMs)} · ${clockLabel(startMs)}`
            : windowLabel(startMs, endMs, clip.duration_s, clip.replay_rate)}
          {clip.simulated ? " · simulated" : ""}
        </div>
        <div className="mt-1 text-sm text-bf-muted">
          Saved {savedAtLabel(clip.created_at)}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/recordings"
          search={{ camera: clip.camera_id }}
          className={chipClass(false)}
        >
          <span className="inline-flex items-center gap-1.5">
            <History size={16} /> Playback
          </span>
        </Link>
        {!image && (
          <button
            type="button"
            className={chipClass(false)}
            onClick={() => onPlay(clip)}
          >
            <span className="inline-flex items-center gap-1.5">
              <Play size={16} />
              Play
            </span>
          </button>
        )}
        <button
          type="button"
          className={chipClass(true, busy)}
          disabled={busy}
          onClick={() => (image ? downloadSavedImage(clip) : onRedownload(clip))}
        >
          <span className="inline-flex items-center gap-1.5">
            <Download size={16} />
            {image ? "Download image" : busy ? "Preparing" : "Download clip"}
          </span>
        </button>
        <button
          type="button"
          className={chipClass(false)}
          aria-label="Remove from list"
          onClick={() => onRemove(clip)}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

export default function Downloads() {
  const { jobs, pauseAll, resumeAll, startDownload } = useRecordingDownloads();
  const { scopeParams, scopeKey } = useScope();
  const qc = useQueryClient();
  const saved = useQuery({
    queryKey: ["saved-clips", scopeKey],
    queryFn: () => api.downloads.list(scopeParams),
  });

  const activeJobs = jobs.filter(
    (job) => job.status === "preparing" || job.status === "paused",
  );
  const otherJobs = jobs.filter(
    (job) => job.status === "failed" || job.status === "cancelled",
  );
  const [hiddenClipIds, setHiddenClipIds] = useState<Set<number>>(() => new Set());
  const [hiddenJobIds, setHiddenJobIds] = useState<Set<string>>(() => new Set());
  const [playingSaved, setPlayingSaved] = useState<SavedClip | null>(null);
  const clips = (saved.data?.clips ?? []).filter((clip) => !hiddenClipIds.has(clip.id));
  const visibleActive = activeJobs.filter((job) => !hiddenJobIds.has(job.id));
  const visibleOther = otherJobs.filter((job) => !hiddenJobIds.has(job.id));
  const hasPreparing = visibleActive.some((job) => job.status === "preparing");
  const hasPaused = visibleActive.some((job) => job.status === "paused");
  const hasVisible =
    visibleActive.length > 0 || visibleOther.length > 0 || clips.length > 0;
  const empty = !hasVisible && !saved.isLoading;

  function removeClip(clip: SavedClip) {
    setHiddenClipIds((prev) => {
      const next = new Set(prev);
      next.add(clip.id);
      return next;
    });
    void api.downloads.remove(clip.id, scopeParams).then(
      () => {
        void qc.invalidateQueries({ queryKey: ["saved-clips"] });
      },
      () => {
        setHiddenClipIds((prev) => {
          const next = new Set(prev);
          next.delete(clip.id);
          return next;
        });
      },
    );
  }

  function redownload(clip: SavedClip) {
    startDownload({
      cameraId: clip.camera_id,
      cameraName: clip.camera_name,
      startMs: clip.start_ms,
      endMs: clip.end_ms,
      preS: clip.pre_s,
      postS: clip.post_s,
      rate: clip.replay_rate || DEFAULT_REPLAY_RATE,
      thumbUrl: clip.has_thumb ? api.downloads.thumbUrl(clip.id) : undefined,
      view: clip.view ?? null,
    });
  }

  function isPreparing(clip: SavedClip): boolean {
    return jobs.some(
      (job) =>
        job.status === "preparing" &&
        job.cameraId === clip.camera_id &&
        job.startMs === clip.start_ms &&
        job.endMs === clip.end_ms &&
        job.preS === clip.pre_s &&
        job.postS === clip.post_s,
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Saved clips"
        description="Open images and clips you saved from search or playback."
        icon={Download}
        actions={
          hasVisible && (
            <button
              type="button"
              onClick={() => {
                setHiddenClipIds(new Set(clips.map((clip) => clip.id)));
                setHiddenJobIds(
                  new Set([...visibleActive, ...visibleOther].map((job) => job.id)),
                );
              }}
              className="bf-button bg-white/10 bg-none"
            >
              Clear
            </button>
          )
        }
      />

      {visibleActive.length > 0 && (hasPreparing || hasPaused) && (
        <div className="flex flex-wrap items-center gap-2">
          {hasPreparing && (
            <button
              type="button"
              className={chipClass(false)}
              onClick={pauseAll}
            >
              <span className="inline-flex items-center gap-1.5">
                <Pause size={16} />
                Pause all
              </span>
            </button>
          )}
          {hasPaused && (
            <button
              type="button"
              className={chipClass(false)}
              onClick={resumeAll}
            >
              <span className="inline-flex items-center gap-1.5">
                <Play size={16} />
                Resume all
              </span>
            </button>
          )}
        </div>
      )}

      {saved.isError && (
        <p className="text-sm text-red-300">Could not load saved clips.</p>
      )}

      {empty ? (
        <div className="flex h-[40vh] flex-col items-center justify-center gap-3 rounded-xl border border-white/5 bg-bf-surface/30 text-center text-bf-muted">
          <Download size={32} />
          <p className="text-sm">Nothing saved.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleActive.map((job) => (
            <DownloadRow key={job.id} job={job} />
          ))}
          {visibleOther.map((job) => (
            <DownloadRow key={job.id} job={job} />
          ))}
          {clips.map((clip) => (
            <SavedClipRow
              key={clip.id}
              clip={clip}
              busy={isPreparing(clip)}
              onRedownload={redownload}
              onRemove={removeClip}
              onPlay={setPlayingSaved}
            />
          ))}
        </div>
      )}
      {playingSaved && (
        <SavedClipPlayer
          clip={playingSaved}
          onClose={() => setPlayingSaved(null)}
        />
      )}
    </div>
  );
}
